import { WIDGETBASE_CLASS } from 'constants/bounding-box';
import { store, useAppSelector } from 'modules/Editor/store';
import { selectWidget, selectWidgets } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { selectActiveWidgetIds } from 'modules/Editor/store/widgetSelector';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlowCoreContextI,
  FlowCoreProviderProps,
  GetConnectedLinesType,
  OnChangeEvent,
  OnChangeEventTypes,
  SubscriptionType,
  UpdateLineWidget,
} from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { getDOMMatrix, getElementForWidgetId, getParentWidgetElement } from 'utils/dom';
import { proxy } from 'valtio';
import { checkAllConnectedPortsSelected, checkBothPortsSelected } from 'widgets/Lines/LineWidget/LineWidget.helpers';
import { LineWidgetData } from 'widgets/Lines/LineWidget/LineWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';

import {
  MUTATION_OBSERVER_CONFIG,
  MUTATION_OBSERVER_CONFIG_WITH_SUBTREE,
  OBSERVING_SUBTREE_WIDGET_LIST,
} from './FlowCore.config';
import {
  getConnectedLineData,
  getIsActiveWidgetsHasConnectedLine,
  getIsConnectedActiveWidget,
} from './FlowCore.helpers';
import WidgetObserverManager from './WidgetObserverManager';

export const FlowCoreContext = createContext<FlowCoreContextI>({
  value: {
    isObserving: false,
    isActiveWidgetLinesConnected: false,
    isActiveWidgetsHasLineConnected: false,
    flowCoreEvent: { onChange: () => {} },
    getisActiveWidgetsHasLineConnected: () => false,
    getConnectedLineUpdatesToWidget: () => [],
    startFlowMode: () => {},
    stopFlowMode: () => {},
    getConnectedLines: () => [],
  },
});

export const FlowCoreProvider = ({ children }: FlowCoreProviderProps): JSX.Element => {
  // Manage separately to change the observing state according to the bounding box event
  // boundingBox._moveable.isDragging() is function so it's hard to track the updated isDragging state
  const [isObserving, setIsObserving] = useState(false);
  // does the selected widget have lines connected to it
  const [isActiveWidgetLinesConnected, setIsActiveWidgetLinesConnected] = useState(false);
  // are there any lines connected to any of the selected widgets
  const [isActiveWidgetsHasLineConnected, setIsActiveWidgetsHasLineConnected] = useState(false);

  // so that they can be accessed on-demand without a subscription to the value
  const isActiveWidgetsHasLineConnectedRef = useRef<boolean>(isActiveWidgetsHasLineConnected);
  isActiveWidgetsHasLineConnectedRef.current = isActiveWidgetsHasLineConnected;

  const observerManagerInstance = useRef<WidgetObserverManager>(new WidgetObserverManager(MUTATION_OBSERVER_CONFIG));
  const subscriptionList = useRef<SubscriptionType[]>([]);

  const activeWidgetIds = useAppSelector(selectActiveWidgetIds);
  const activePageId = useAppSelector(selectActivePage);

  const flowCoreEvent = useMemo(() => {
    return {
      onChange: (widgetId: WidgetId, callback: OnChangeEvent) => {
        const subscriber = subscriptionList.current.find(([subWidgetId]) => subWidgetId === widgetId);
        if (subscriber) {
          subscriber[1] = callback;
        } else {
          subscriptionList.current.push([widgetId, callback]);
        }
      },
    };
  }, []);

  // Calculate the matrix of the target using mutationObserver, and trigger callback to the updateConnectedLine of the connected line widget.
  const mutationCallback = useCallback<MutationCallback>(
    (mutationList) => {
      if (mutationList.length) {
        // Since this is an asynchronous function to use getComputedStyled() for single element
        // so only the first record will be executed when it triggered
        const mutation = mutationList[0];
        if (!['attributes', 'childList', 'characterData'].includes(mutation.type)) return;

        let target = mutation.target;

        switch (mutation.type) {
          case 'characterData':
            // For OBSERVING_SUBTREE_WIDGET_LIST
            const parentElement = target.parentElement as HTMLElement;
            target = parentElement.closest(`.${WIDGETBASE_CLASS}`) as HTMLElement;

            break;
          case 'attributes':
          case 'childList':
            // Get widget base for table, text widget that is using subtree: true
            if (
              !(target as HTMLElement).classList.contains(WIDGETBASE_CLASS) &&
              getWidgetTypeFromId((target as HTMLElement).id) !== WidgetType.Group
            ) {
              target = (target as HTMLElement).closest(`.${WIDGETBASE_CLASS}`) as HTMLElement;
            }
            break;
        }

        if (!(target instanceof HTMLElement)) return;

        const widgetId = target.id;

        const allTargetWidgetData = observerManagerInstance.current.getAllByParentWidgetId(widgetId);
        if (!allTargetWidgetData) return;

        const matrix = getDOMMatrix(target);

        const widgets = selectWidgets(store.getState());
        const connectedLineData = getConnectedLineData(widgets);

        allTargetWidgetData.forEach((targetWidgetData) => {
          connectedLineData[targetWidgetData.widgetId]?.forEach((lineWidgetId) => {
            // If both ports are selected, rely on moveable drag events instead of using the listener to update line position.
            // Otherwise, will have laggy performance
            const bothPortsConnected = checkBothPortsSelected(lineWidgetId);
            const allPortsConnected = checkAllConnectedPortsSelected(lineWidgetId);
            if (bothPortsConnected || (allPortsConnected && activeWidgetIds.includes(lineWidgetId))) return;

            const subscriber = subscriptionList.current.find(([widgetId]) => widgetId === lineWidgetId);
            if (subscriber && typeof subscriber?.[1] === 'function') {
              const setDelta = subscriber[1];
              setDelta(OnChangeEventTypes.Update, matrix, targetWidgetData.element);
            }
          });
        });
      }
    },
    [activeWidgetIds],
  );

  const getConnectedLines = useCallback<GetConnectedLinesType>(
    (widgetId, target, shouldCheckConnection = true): UpdateLineWidget[] => {
      if (!widgetId) return [];
      if (!target) return [];

      const widgets = selectWidgets(store.getState());
      const connectedLineData = getConnectedLineData(widgets);

      const connectedLineIds = connectedLineData?.[widgetId];
      if (!connectedLineIds || connectedLineIds.length === 0) return [];

      const activeWidgetIds = selectActiveWidgetIds(store.getState());

      const matrix = getDOMMatrix(target as HTMLElement);

      // Generate line data
      const result = connectedLineIds
        .map(
          (lineWidgetId) =>
            subscriptionList.current.find(([widgetId]) => widgetId === lineWidgetId) as SubscriptionType,
        )
        .filter((subscriber) => {
          // If both ports are selected, save logic is handled by moveable
          const isBothPortsConnected = checkBothPortsSelected(subscriber[0]);
          const isAllPortsSelected = checkAllConnectedPortsSelected(subscriber[0]);
          return (
            !!subscriber &&
            (!shouldCheckConnection ||
              !(isBothPortsConnected || (isAllPortsSelected && activeWidgetIds.includes(subscriber[0]))))
          );
        })
        .map(([widgetId, generateLineWidgetData]) => ({
          widgetId: widgetId,
          widgetData: generateLineWidgetData(OnChangeEventTypes.Done, matrix, target) as LineWidgetData,
        }));

      return result;
    },
    [],
  );

  // Gets the connected line updates for a widget and its group if it belongs to one.
  const getConnectedLineUpdatesToWidget = useCallback(
    (widgetId: WidgetId): UpdateLineWidget[] => {
      // Update the connected line widgets to any group this text widget happens to belong to
      const parentId = groupIdCache.getParentIdNested(widgetId);
      if (parentId) {
        const target = getElementForWidgetId(parentId);
        const groupWidget = selectWidget(parentId)(store.getState());
        if (groupWidget && target) {
          return getConnectedLines(parentId, target as HTMLElement);
        }
      } else {
        const target = getElementForWidgetId(widgetId);
        if (target) {
          return getConnectedLines(widgetId, target as HTMLElement);
        }
      }
      return [] as UpdateLineWidget[];
    },
    [getConnectedLines],
  );

  // when a widget is selected
  useEffect(() => {
    const widgets = selectWidgets(store.getState());

    setIsActiveWidgetLinesConnected(getIsConnectedActiveWidget(activeWidgetIds));
    setIsActiveWidgetsHasLineConnected(getIsActiveWidgetsHasConnectedLine(activeWidgetIds, widgets));

    const connectedLineData = getConnectedLineData(widgets);

    activeWidgetIds.forEach((widgetId) => {
      if (!connectedLineData?.[widgetId]) return;
      const connectedLineIds = connectedLineData?.[widgetId];

      connectedLineIds.forEach((lineWidgetId) => {
        const isSubscribed = subscriptionList.current.some(([widgetId]) => widgetId === lineWidgetId);
        if (observerManagerInstance?.current && isSubscribed) {
          const element = getElementForWidgetId(widgetId);
          if (element) {
            const responsiveWidgetElement = getParentWidgetElement(element) ?? undefined;
            observerManagerInstance.current.add(
              widgetId,
              element,
              mutationCallback,
              responsiveWidgetElement,
              responsiveWidgetElement?.id,
            );
          }
        }
      });

      if (isObserving) {
        // Observe widget when it calls startFlowMode()
        const hasConnectedLine = connectedLineIds?.length > 0;
        if (!hasConnectedLine) return;

        if (OBSERVING_SUBTREE_WIDGET_LIST.includes(getWidgetTypeFromId(widgetId))) {
          observerManagerInstance?.current?.observe(widgetId, MUTATION_OBSERVER_CONFIG_WITH_SUBTREE);
        } else {
          observerManagerInstance?.current?.observe(widgetId);
        }
      } else {
        // Update callback when it calls stopFlowMode()
        connectedLineIds.forEach((lineWidgetId) => {
          const isSubscribed = subscriptionList.current.some(([widgetId]) => widgetId === lineWidgetId);
          if (observerManagerInstance?.current && isSubscribed) {
            observerManagerInstance.current.setCallback(widgetId, mutationCallback);
          }
        });
      }
    });
  }, [activeWidgetIds, isObserving, mutationCallback]);

  // Reset the observer manager instance when the active page changes
  useEffect(() => {
    if (activePageId) observerManagerInstance.current.reset();
  }, [activePageId]);

  const startFlowMode = useCallback(() => {
    setIsObserving(true);
  }, []);

  const stopFlowMode = useCallback(() => {
    setIsObserving(false);
  }, []);

  const getisActiveWidgetsHasLineConnected = useCallback(() => {
    return isActiveWidgetsHasLineConnectedRef.current;
  }, []);

  const value = {
    isObserving,
    flowCoreEvent,
    isActiveWidgetLinesConnected,
    isActiveWidgetsHasLineConnected,
    getisActiveWidgetsHasLineConnected,
    getConnectedLines,
    getConnectedLineUpdatesToWidget,
    startFlowMode,
    stopFlowMode,
  };

  const state = useRef<FlowCoreContextI>(proxy({ value })).current;
  // sync the values with the proxy ref
  state.value = value;

  return <FlowCoreContext.Provider value={state}>{children}</FlowCoreContext.Provider>;
};
